import { useTranslation } from 'react-i18next';

import { BetSummaryProps, BetSummarySectionProps } from '@/components/BetSummarySection';
import { TextProps } from '@/components/TextComponent';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { useBetCost } from '@/feature/bets-sbk/hooks/use-bet-cost';
import { OddsFormat } from '@/hooks/use-auth-user-settings';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useOddsFormat } from '@/hooks/use-odds-format';
import { toTitleCase } from '@/utils/toTitleCase';

// Adjust the odds based on the odds format
export const getAdjustedOdds = (bet: Bet, oddsFormat: OddsFormat) => {
    if (bet.is_betr_bucks && oddsFormat === OddsFormat.AMERICAN) {
        return bet.current_odds + 1;
    }
    if (bet.is_betr_bucks && oddsFormat === OddsFormat.DECIMAL) {
        return bet.current_odds;
    }
    return bet.requested_odds;
};

/**
 * Shared bet summary props for all Bet Cards
 */
export const useSharedBetSummaryProps = ({ bet, betSummarySectionProps }: BetSummaryProps) => {
    const { t } = useTranslation(['common', 'bets']);
    const oddsFormat = useOddsFormat();
    const isPredictions = useIsPredictionsProduct();
    const stake = useBetCost(bet);
    // Predictions positions show "Cost" with plain dollar amounts; bonus bets mark the stake with the Betr Bucks icon.
    const body1Icon = isPredictions
        ? ('none' as const)
        : bet.is_betr_bucks
        ? ('bonus' as const)
        : ('currency' as const);
    const body3Icon = isPredictions ? ('none' as const) : ('currency' as const);
    return {
        header1Text: isPredictions ? t('bets:cost') : t('entry'),
        header2Text: isPredictions ? t('price') : t('odds'),
        header3Text: t('payout'),
        stake,
        multiplier: getAdjustedOdds(bet, oddsFormat),
        betSummarySeparatorColour: 'neutral800' as const,
        body1Icon,
        body3Icon,
        ...betSummarySectionProps,
    };
};

/**
 * Handles 'Voided' bet summary for all Bet Cards
 */
export const useVoidedBetSummary = (
    bet: Bet,
    voidedTextColor: TextProps['color'] = 'neutral300'
): BetSummarySectionProps => {
    const { t } = useTranslation('bets');
    return {
        ...useSharedBetSummaryProps({ bet }),
        header3Text: undefined,
        multiplier: undefined,
        body1TextDecorationLine: 'line-through' as const,
        header1TextColor: voidedTextColor,
        header2TextColor: voidedTextColor,
        body1TextColor: voidedTextColor,
        body3TextColor: voidedTextColor,
        body3Icon: 'none',
        strikeThroughMultiplierColor: voidedTextColor,
        strikeThroughMultiplier: bet.requested_odds,
        body3Text: toTitleCase(t('voided')),
    };
};
